import { useState, useCallback } from 'react'
import { api } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'
import { showErrorToast, showSuccessToast } from '../utils/toast'

export function useSpeakers() {
  const [loading, setLoading] = useState(false)

  // Lister tous les intervenants (Public)
  const getSpeakers = useCallback(async (options = {}) => {
    const { page = 1, limit = 20 } = options
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      })
      const data = await api.get(`${endpoints.speakers}?${params.toString()}`, {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement des intervenants'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Obtenir un intervenant (Public)
  const getSpeaker = useCallback(async (speakerId) => {
    setLoading(true)
    try {
      const data = await api.get(endpoints.getSpeaker(speakerId), {
        isPublicRoute: true
      })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors du chargement de l\'intervenant'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Créer un intervenant (Admin) - Simplifié : seulement nom, title et photo
  const createSpeaker = useCallback(async (speakerData) => {
    setLoading(true)
    try {
      // Vérifier si un fichier photo est présent
      const hasPhotoFile = speakerData.photoFile instanceof File || speakerData.photoFile instanceof Blob
      
      let requestData
      
      if (hasPhotoFile) {
        // Utiliser FormData pour l'upload de fichier
        const formData = new FormData()
        
        // Champs obligatoires
        if (speakerData.name) formData.append('name', speakerData.name)
        
        // Champs optionnels
        if (speakerData.title) formData.append('title', speakerData.title)
        
        // Fichier photo
        if (hasPhotoFile) {
          formData.append('photo', speakerData.photoFile)
        }
        
        requestData = formData
      } else {
        // Utiliser JSON normal
        requestData = {
          name: speakerData.name,
          ...(speakerData.title && { title: speakerData.title })
        }
      }
      
      const data = await api.post(endpoints.createSpeaker, requestData)
      showSuccessToast({ message: 'Intervenant créé avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de la création de l\'intervenant'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Modifier un intervenant (Admin) - Simplifié : seulement nom, title et photo
  const updateSpeaker = useCallback(async (speakerId, speakerData) => {
    setLoading(true)
    try {
      // Vérifier si un fichier photo est présent
      const hasPhotoFile = speakerData.photoFile instanceof File || speakerData.photoFile instanceof Blob
      
      let requestData
      
      if (hasPhotoFile) {
        // Utiliser FormData pour l'upload de fichier
        const formData = new FormData()
        
        if (speakerData.name !== undefined) formData.append('name', speakerData.name || '')
        if (speakerData.title !== undefined) formData.append('title', speakerData.title || '')
        
        // Fichier photo
        if (hasPhotoFile) {
          formData.append('photo', speakerData.photoFile)
        } else if (speakerData.photo !== undefined) {
          // Si c'est une URL ou null, l'ajouter
          formData.append('photo', speakerData.photo || '')
        }
        
        requestData = formData
      } else {
        // Utiliser JSON normal
        requestData = {
          ...(speakerData.name !== undefined && { name: speakerData.name }),
          ...(speakerData.title !== undefined && { title: speakerData.title || null })
        }
      }
      
      const data = await api.put(endpoints.updateSpeaker(speakerId), requestData)
      showSuccessToast({ message: 'Intervenant modifié avec succès' })
      return data
    } catch (error) {
      const message = error?.message || 'Erreur lors de la modification de l\'intervenant'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Supprimer un intervenant (Admin)
  const deleteSpeaker = useCallback(async (speakerId) => {
    setLoading(true)
    try {
      await api.delete(endpoints.deleteSpeaker(speakerId))
      showSuccessToast({ message: 'Intervenant supprimé avec succès' })
    } catch (error) {
      const message = error?.message || 'Erreur lors de la suppression de l\'intervenant'
      showErrorToast({ message })
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    getSpeakers,
    getSpeaker,
    createSpeaker,
    updateSpeaker,
    deleteSpeaker
  }
}

